<template>
  <div v-loading="isLoading">
    <template v-if="notes.length">
      <template v-if="direction === 'horizontal'">
        <el-row :gutter="15">
          <el-col
            v-for="(note, index) in notes"
            :key="index"
            :span="24 / notes.length"
          >
            <el-card shadow="never">
              <Note
                :note="note"
                :truncate="truncate"
              />
            </el-card>
          </el-col>
        </el-row>
      </template>

      <template v-else-if="direction === 'vertical'">
        <template v-for="(note, index) in notes">
          <Note
            :key="note.id"
            :note="note"
            :truncate="truncate"
          />

          <el-divider
            v-if="(index + 1) !== notes.length"
            :key="index"
          />
        </template>
      </template>
    </template>

    <p
      v-else
      class="p-3 m-0 text-muted text-center"
    >
      No Data
    </p>

    <el-pagination
      v-if="paginate"
      class="text-center pt-3"
      :current-page.sync="notes.page"
      :page-size.sync="notes.limit"
      :layout="paginationLayout"
      :total="notes.total"
      @size-change="reload"
      @current-change="reload"
    />
    <el-divider v-if="!isLoading" />
    <div class="text-center pb-3">
      <el-button
        type="primary"
        icon="el-icon-plus"
        @click.native="create"
      >
        Add Note
      </el-button>
    </div>
  </div>
</template>

<script>
import Resource from '@/plugins/sdk/Resource'
import Note from '@/components/Notes/Note'
import NoteCreateDialog from '@/components/Notes/Note/CreateDialog'
import Vue from 'vue'

export default {
  components: {
    Note
  },
  props: {
    resource: {
      type: [ Object, Function, String ],
      required: true
    },
    identifier: {
      type: String,
      required: false,
      default: undefined
    },
    limit: {
      type: Number,
      required: false,
      default: 20
    },
    paginate: {
      type: Boolean,
      required: false,
      default: false
    },
    paginationLayout: {
      type: String,
      required: false,
      default: 'total, sizes, prev, pager, next, jumper'
    },
    truncate: {
      type: Boolean,
      required: false,
      default: false
    },
    direction: {
      type: String,
      required: false,
      default: 'vertical',
      validator: function (value) {
        return [ 'vertical', 'horizontal' ].indexOf(value) !== -1
      }
    }
  },
  data () {
    return {
      isLoading: true,
      noteCreateFormDialogInstance: undefined,
      noteCreateFormDialogVisible: false,
      notes: []
    }
  },
  computed: {
    r () {
      let resource = this.resource

      if (this.resource instanceof Resource) {
        resource = this.resource.constructor.name
      } else if (this.resource.prototype instanceof Resource) {
        resource = this.resource.name
      }

      return resource
    },
    i () {
      let identifier = this.identifier

      if (this.resource instanceof Resource) {
        identifier = this.resource[this.resource.constructor.identifier]
      }

      return identifier
    }
  },
  watch: {
    noteCreateFormDialogVisible (visible) {
      if (!visible && typeof this.noteCreateFormDialogInstance !== 'undefined') {
        this.noteCreateFormDialogInstance.$destroy()
        this.noteCreateFormDialogInstance.$el.remove()
        this.noteCreateFormDialogInstance = undefined
      }
    }
  },
  created () {
    this.load()
  },
  methods: {
    load () {
      this
        .$sdk
        .note
        .list(request => {
          request.limit = this.limit
          request.filters = {
            resource: `${this.r}:${this.i}`
          }
        })
        .then(notes => {
          this.notes = notes
        })
        .finally(() => { this.isLoading = false })
    },
    create () {
      const NoteCreateDialogClass = Vue.extend(NoteCreateDialog)

      this.noteCreateFormDialogInstance = new NoteCreateDialogClass({
        propsData: {
          note: Object.assign(new this.$sdk.note(), {
            resource: this.r,
            resource_uuid: this.i
          })
        }
      })

      this
        .noteCreateFormDialogInstance
        .$on('destroy', () => {
          this.noteCreateFormDialogVisible = false
        })

      this
        .noteCreateFormDialogInstance
        .$on('created-note', () => {
          this.reload()
        })

      this
        .noteCreateFormDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.noteCreateFormDialogInstance.$el)

      this.noteCreateFormDialogVisible = true
    },
    async reload () {
      this.isLoading = true

      try {
        await this.notes.reload(request => {
          request.filters = {
            resource: `${this.r}:${this.i}`
          }
        })
      } catch {
        this.$message.error('Failed to load Notes')
      }

      this.isLoading = false
    },
    reset () {
      this.notes = []
    }
  }
}
</script>
